// components/DashboardHeader.js
import React from 'react';
import { Box, Typography, Button, Avatar, Paper, Grid, Card, CardContent, LinearProgress } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EmailIcon from '@mui/icons-material/Email';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const DashboardHeader = ({ licenses, reportCount, rows }) => {
  // Calculate additional stats
  const totalUsers = rows?.length || 0;
  const pendingTests = rows?.filter(user => !user.USER_RESPONCE)?.length || 0;
  const completedTests = rows?.filter(user => user.USER_RESPONCE)?.length || 0;
  const completionRate = totalUsers > 0 ? Math.round((completedTests / totalUsers) * 100) : 0;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Welcome Section */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Dashboard Overview
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Monitor your assessment progress and manage user data
        </Typography>
      </Box>

      {/* Stats Cards Grid */}
      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: 2 }}>
        {/* Total Users Card */}
        <Grid item xs={6} sm={3}>
          <Card 
            elevation={1}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              height: { xs: 80, sm: 100 },
              minHeight: { xs: 80, sm: 100 },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: { xs: '40px', sm: '60px' },
                height: { xs: '40px', sm: '60px' },
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                transform: { xs: 'translate(15px, -15px)', sm: 'translate(20px, -20px)' },
              }
            }}
          >
            <CardContent sx={{ 
              p: { xs: 1, sm: 2 }, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              '&:last-child': { pb: { xs: 1, sm: 2 } }
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    opacity: 0.9, 
                    mb: { xs: 0.25, sm: 0.5 }, 
                    fontWeight: 500, 
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}>
                    Total Users
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ 
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    fontSize: { xs: '1.25rem', sm: '1.75rem' }
                  }}>
                    {totalUsers}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ 
                  fontSize: { xs: 18, sm: 24 }, 
                  opacity: 0.9, 
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' 
                }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Completed Tests Card */}
        <Grid item xs={6} sm={3}>
          <Card 
            elevation={1}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              height: { xs: 80, sm: 100 },
              minHeight: { xs: 80, sm: 100 },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: { xs: '40px', sm: '60px' },
                height: { xs: '40px', sm: '60px' },
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                transform: { xs: 'translate(15px, -15px)', sm: 'translate(20px, -20px)' },
              }
            }}
          >
            <CardContent sx={{ 
              p: { xs: 1, sm: 2 }, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              '&:last-child': { pb: { xs: 1, sm: 2 } }
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    opacity: 0.9, 
                    mb: { xs: 0.25, sm: 0.5 }, 
                    fontWeight: 500, 
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}>
                    Completed
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ 
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    fontSize: { xs: '1.25rem', sm: '1.75rem' }
                  }}>
                    {completedTests}
                  </Typography>
                </Box>
                <AssessmentIcon sx={{ 
                  fontSize: { xs: 18, sm: 24 }, 
                  opacity: 0.9, 
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' 
                }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pending Tests Card */}
        <Grid item xs={6} sm={3}>
          <Card 
            elevation={1}
            sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              height: { xs: 80, sm: 100 },
              minHeight: { xs: 80, sm: 100 },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: { xs: '40px', sm: '60px' },
                height: { xs: '40px', sm: '60px' },
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                transform: { xs: 'translate(15px, -15px)', sm: 'translate(20px, -20px)' },
              }
            }}
          >
            <CardContent sx={{ 
              p: { xs: 1, sm: 2 }, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              '&:last-child': { pb: { xs: 1, sm: 2 } }
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    opacity: 0.9, 
                    mb: { xs: 0.25, sm: 0.5 }, 
                    fontWeight: 500, 
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}>
                    Pending
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ 
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    fontSize: { xs: '1.25rem', sm: '1.75rem' }
                  }}>
                    {pendingTests}
                  </Typography>
                </Box>
                <EmailIcon sx={{ 
                  fontSize: { xs: 18, sm: 24 }, 
                  opacity: 0.9, 
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' 
                }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* License Status Card */}
        <Grid item xs={6} sm={3}>
          <Card 
            elevation={1}
            sx={{
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
              color: 'white',
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              height: { xs: 80, sm: 100 },
              minHeight: { xs: 80, sm: 100 },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: { xs: '40px', sm: '60px' },
                height: { xs: '40px', sm: '60px' },
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                transform: { xs: 'translate(15px, -15px)', sm: 'translate(20px, -20px)' },
              }
            }}
          >
            <CardContent sx={{ 
              p: { xs: 1, sm: 2 }, 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'space-between',
              '&:last-child': { pb: { xs: 1, sm: 2 } }
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    opacity: 0.9, 
                    mb: { xs: 0.25, sm: 0.5 }, 
                    fontWeight: 500, 
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}>
                    Licenses
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ 
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    fontSize: { xs: '1.25rem', sm: '1.75rem' }
                  }}>
                    {licenses.remaining}/{licenses?.total}
                  </Typography>
                </Box>
                <VerifiedIcon sx={{ 
                  fontSize: { xs: 18, sm: 24 }, 
                  opacity: 0.9, 
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' 
                }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardHeader;
